import bindAll from 'lodash.bindall';
import PropTypes from 'prop-types';
import React from 'react';
import {connect} from 'react-redux';
import {defineMessages, injectIntl, intlShape} from 'react-intl';
import VM from 'scratch-vm';

import costumeLibraryContent from '../lib/libraries/costumes.json';
import spriteTags from '../lib/libraries/sprite-tags';
import LibraryComponent from '../components/library/library.jsx';
import {fetchBrickLabGlobalImages} from '../reducers/bricklab-global-images';
import {getImageDisplayUrl} from '../lib/bricklab-image-utils';

const messages = defineMessages({
    libraryTitle: {
        defaultMessage: 'Choose a Costume',
        description: 'Heading for the costume library',
        id: 'gui.costumeLibrary.chooseACostume'
    }
});


class CostumeLibrary extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleItemSelected',
            'loadGlobalImageAsset'
        ]);
    }
    
    componentDidMount () {
        // Refetch when the costume library opens (remounts after close). Same rationale as sprite-library.jsx.
        const {loading} = this.props.bricklabGlobalImages;
        if (!loading) {
            const thunkAction = fetchBrickLabGlobalImages(true);
            thunkAction(this.props.dispatch, () => this.props.bricklabGlobalImages);
        }
    }
    
    /**
     * Load image from signed URL and create VM asset
     * Similar to how sprite-library.jsx handles rawURL assets
     * @param {object} image - Image object with imageUrl
     * @returns {Promise<object>} Asset data with asset, assetId, md5ext, dataFormat, and image dimensions
     */
    async loadGlobalImageAsset (image) {
        const {vm} = this.props;
        const storageInstance = vm.runtime.storage;
        
        if (!storageInstance) {
            throw new Error('VM storage not available');
        }
        
        // Get full state for getImageDisplayUrl
        const fullState = {scratchGui: {bricklabGlobalImages: this.props.bricklabGlobalImages}};
        const imageUrl = getImageDisplayUrl(image, fullState);
        if (!imageUrl) {
            throw new Error('Image URL not available');
        }
        
        // Fetch the image through SEEDS API proxy (avoids CORS issues)
        const response = await fetch(imageUrl, {
            method: 'GET',
            credentials: 'include', // Include credentials for SEEDS API
            headers: {
                'Content-Type': 'application/json'
            }
        });
        if (!response.ok) {
            throw new Error(`Failed to fetch image: ${response.statusText}`);
        }
        
        // Determine format from URL or response
        const contentType = response.headers.get('content-type') || '';
        const urlPath = new URL(imageUrl).pathname.toLowerCase();
        let dataFormat = 'png'; // default
        let assetType = storageInstance.AssetType.ImageBitmap;
        
        if (contentType.includes('svg') || urlPath.endsWith('.svg')) {
            dataFormat = 'svg';
            assetType = storageInstance.AssetType.ImageVector;
        } else if (
            contentType.includes('jpeg') ||
            contentType.includes('jpg') ||
            urlPath.endsWith('.jpg') ||
            urlPath.endsWith('.jpeg')
        ) {
            dataFormat = 'jpg';
            assetType = storageInstance.AssetType.ImageBitmap;
        } else if (contentType.includes('png') || urlPath.endsWith('.png')) {
            dataFormat = 'png';
            assetType = storageInstance.AssetType.ImageBitmap;
        } else if (contentType.includes('gif') || urlPath.endsWith('.gif')) {
            dataFormat = 'gif';
            assetType = storageInstance.AssetType.ImageBitmap;
        }

        // Get image data
        let imageData;
        if (dataFormat === 'svg') {
            const text = await response.text();
            // Sanitize SVG
            const {sanitizeByteStream} =
                require('scratch-svg-renderer');
            const svgBytes = new TextEncoder().encode(text);
            imageData = new Uint8Array(sanitizeByteStream(svgBytes));
        } else {
            // For bitmaps, use the image data as-is
            // Image resizing should be handled on the SEEDS platform when admins upload images
            const arrayBuffer = await response.arrayBuffer();
            imageData = new Uint8Array(arrayBuffer);
        }
        
        // Create asset in VM storage
        const asset = storageInstance.createAsset(
            assetType,
            dataFormat,
            imageData,
            null,
            true // generate md5
        );
        
        return {
            asset: asset,
            assetId: asset.assetId,
            md5ext: `${asset.assetId}.${dataFormat}`,
            dataFormat: dataFormat
        };
    }
    
    async handleItemSelected (item) {
        // Check if this is a global image (has imageUrl or isGlobal flag)
        if (item.isGlobalImage || item.imageUrl) {
            try {
                // Load the image asset from URL
                const assetData = await this.loadGlobalImageAsset(item);
                
                // Create VM costume object - DO NOT set rotationCenterX/Y unless explicitly provided
                // Let the renderer calculate it automatically (same as file uploads do)
                const vmCostume = {
                    name: item.name,
                    bitmapResolution: item.bitmapResolution || 1,
                    skinId: null,
                    ...assetData // Include asset, assetId, md5ext, dataFormat
                };
                
                // Only set rotation center if explicitly provided in item metadata AND not 0
                // Treating 0,0 as "not provided" since that's the default we were setting
                // Otherwise leave undefined so renderer calculates it automatically (center of image)
                if (typeof item.rotationCenterX === 'number' && !isNaN(item.rotationCenterX) &&
                    typeof item.rotationCenterY === 'number' && !isNaN(item.rotationCenterY) &&
                    !(item.rotationCenterX === 0 && item.rotationCenterY === 0)) {
                    vmCostume.rotationCenterX = item.rotationCenterX;
                    vmCostume.rotationCenterY = item.rotationCenterY;
                }
                // If not provided (or 0,0), rotationCenterX/Y will be undefined, and the renderer
                // will automatically calculate the center (same behavior as file uploads)
                
                // Use addCostume (not addCostumeFromLibrary) since we have the asset
                await this.props.vm.addCostume(vmCostume.md5ext, vmCostume);
            } catch (error) {
                // TODO: Show error alert to user
                console.error('Error loading global image:', error);
            }
        } else {
            // Standard library costume
            const vmCostume = {
                name: item.name,
                rotationCenterX: item.rotationCenterX,
                rotationCenterY: item.rotationCenterY,
                bitmapResolution: item.bitmapResolution,
                skinId: null
            };
            this.props.vm.addCostumeFromLibrary(item.md5ext, vmCostume);
        }
    }
    
    /**
     * Get dynamic tags from global image categories
     * @returns {Array} Array of tag objects for category tabs
     */
    getDynamicTags () {
        const {categories} = this.props.bricklabGlobalImages;
        // Convert category names to lowercase tags (matching Scratch's tag format)
        return categories.map(category => ({
            tag: category.name.toLowerCase(),
            intlLabel: {
                id: `gui.libraryTags.${category.name.toLowerCase()}`,
                defaultMessage: category.name
            }
        }));
    }

    /**
     * Transform global images into library format
     * @returns {Array} Combined array of static costumes and global images
     */
    getCombinedLibraryData () {
        const {categories} = this.props.bricklabGlobalImages;
        const staticCostumes = costumeLibraryContent;

        // Transform global images to library format
        const globalImages = categories.flatMap(category =>
            category.images.map(image => {
                // Get full state for getImageDisplayUrl
                const fullState = {
                    scratchGui: {bricklabGlobalImages: this.props.bricklabGlobalImages}
                };
                const imageUrl = getImageDisplayUrl(image, fullState);
                // Preserve the S3 key for later use
                // (either from s3Key or imageUrl if it's not a URL)
                const s3Key = image.s3Key ||
                    (image.imageUrl && !image.imageUrl.startsWith('http') ?
                        image.imageUrl :
                        null);
                return {
                    name: image.name,
                    md5ext: image.id, // Use image ID as unique identifier
                    rawURL: imageUrl, // Use rawURL for display (proxy URL)
                    isGlobalImage: true,
                    imageUrl: imageUrl, // Proxy URL
                    s3Key: s3Key, // Preserve original S3 key
                    category: category.name.toLowerCase(), // Category for grouping in library (required for library.jsx grouping)
                    tags: [category.name.toLowerCase()], // Add as tag for filtering
                    // DO NOT set rotationCenterX/Y - leave undefined so renderer calculates center
                    bitmapResolution: 1
                };
            })
        );

        // Combine static costumes with global images
        // Static costumes don't have category, so they'll appear in "All"
        return [...staticCostumes, ...globalImages];
    }
    
    render () {
        const combinedData = this.getCombinedLibraryData();
        const dynamicTags = this.getDynamicTags();
        // Combine static tags with dynamic category tags
        const allTags = [...spriteTags, ...dynamicTags];

        return (
            <LibraryComponent
                data={combinedData}
                id="costumeLibrary"
                tags={allTags}
                categoriesLoading={this.props.bricklabGlobalImages.loading}
                title={this.props.intl.formatMessage(messages.libraryTitle)}
                onItemSelected={this.handleItemSelected}
                onRequestClose={this.props.onRequestClose}
            />
        );
    }
}

CostumeLibrary.propTypes = {
    intl: intlShape.isRequired,
    onRequestClose: PropTypes.func,
    vm: PropTypes.instanceOf(VM).isRequired,
    dispatch: PropTypes.func.isRequired,
    bricklabGlobalImages: PropTypes.shape({
        categories: PropTypes.array,
        loading: PropTypes.bool,
        error: PropTypes.string,
        lastFetched: PropTypes.number,
        imageUrlMap: PropTypes.object
    })
};

const mapStateToProps = state => ({
    bricklabGlobalImages: state.scratchGui.bricklabGlobalImages
});

export default connect(mapStateToProps)(injectIntl(CostumeLibrary));
